<!-- app-subdomain-creators\src\Components\NewsfeedPostScheduler.vue -->

<template>
  <div ref="rootEl" class="space-y-4">
    <!-- Date / Time cards -->
    <div class="relative">
      <div class="flex gap-3">
        <!-- DATE CARD -->
        <button
          type="button"
          class="flex-1 flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm
                 transition-colors duration-150"
          :class="[
            showDatePicker
              ? 'border-[#2D8CFF] bg-blue-50'
              : 'border-zinc-300 bg-white hover:bg-zinc-50'
          ]"
          @click="toggleDatePicker"
        >
          <div class="flex items-center gap-3">
            <CalendarDaysIcon class="w-6 text-zinc-500" />
            <div class="flex flex-col items-start leading-tight">
              
              <span
                class="text-base"
                :class="selectedDate ? 'text-zinc-800' : 'text-zinc-400'"
              >
                {{ dateLabel }}
              </span>
            </div>
          </div>
        </button>

        <!-- TIME CARD -->
        <button
          type="button"
          class="flex-1 flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm
                 transition-colors duration-150"
          :class="[
            showTimePicker
              ? 'border-[#2D8CFF] bg-blue-50'
              : 'border-zinc-300 bg-white hover:bg-zinc-50'
          ]"
          @click="toggleTimePicker"
        >
          <div class="flex items-center gap-3">
            <ClockIcon class="w-6 text-zinc-500" />
            <div class="flex flex-col items-start leading-tight">
            
              <span
                class="text-base"
                :class="selectedTime ? 'text-zinc-800' : 'text-zinc-400'"
              >
                {{ timeLabel }}
              </span>
            </div>
          </div>
        </button>
      </div>

      <!-- DATE DROPDOWN -->
      <div
        v-if="showDatePicker"
        class="absolute left-0 mt-2 w-[260px] rounded-2xl bg-white shadow-2xl p-3 z-40 border border-zinc-100"
      >
        <!-- Month header -->
        <div class="flex items-center justify-between mb-3">
          <button
            type="button"
            class="p-1 rounded-full hover:bg-zinc-100"
            @click="prevMonth"
          >
            <ChevronLeftIcon class="w-4 h-4 text-zinc-600" />
          </button>

          <span class="text-base font-medium text-zinc-800">
            {{ monthLabel }}
          </span>

          <button
            type="button"
            class="p-1 rounded-full hover:bg-zinc-100"
            @click="nextMonth"
          >
            <ChevronRightIcon class="w-4 h-4 text-zinc-600" />
          </button>
        </div>

        <!-- Weekdays -->
        <div class="grid grid-cols-7 text-[11px] text-center text-zinc-400 mb-1">
          <span v-for="d in weekdayLabels" :key="d">{{ d }}</span>
        </div>

        <!-- Days -->
        <div class="grid grid-cols-7 gap-y-1 text-[13px]">
          <!-- Leading blanks -->
          <span
            v-for="n in leadingBlanks"
            :key="'blank-' + n"
          ></span>

          <!-- Actual days -->
          <button
            v-for="day in daysInMonth"
            :key="day"
            type="button"
            class="mx-auto flex h-8 w-8 items-center justify-center rounded-full
                   transition-colors duration-150"
            :class="dayClasses(day)"
            :disabled="isPastDay(day)"
            @click="pickDate(day)"
          >
            {{ day }}
          </button>
        </div>
      </div>

      <!-- TIME DROPDOWN -->
      <div
        v-if="showTimePicker"
        class="absolute right-0 mt-2 w-[200px] max-h-80 overflow-y-auto chat-scroll rounded-2xl border border-zinc-100 bg-white shadow-2xl py-3 z-40"
      >
        <button
          v-for="t in visibleTimes"
          :key="t.label"
          type="button"
          class="w-full flex items-center justify-between px-3 py-2 text-[13px] text-left font-medium text-zinc-800 transition-colors duration-150 hover:bg-zinc-100"
          @click="pickTime(t)"
        >
          <span>{{ t.label }}</span>
          <span
            v-if="selectedTime && selectedTime.label === t.label"
            class="text-[10px] text-[#2D8CFF] font-semibold"
          >
            Selected
          </span>
        </button>
        <div
          v-if="!visibleTimes.length"
          class="px-3 py-2 text-xs text-zinc-500"
        >
          No available times for the selected date.
        </div>
      </div>



    </div>

    <!-- Schedule button -->
    <button
      v-if="props.showSubmitButton"
      type="button"
      class="w-full mt-2 px-4 py-2 rounded-md text-base font-semibold transition-colors duration-150"
      :class="isValid ? 'bg-[#2D8CFF] text-white hover:bg-[#1f7aeb]' : 'bg-zinc-200 text-zinc-500 cursor-not-allowed'"
      :disabled="!isValid"
      @click="submitSchedule"
    >
      Schedule
    </button>

 <!-- Guard error if selected datetime is in the past -->
    <p
      v-if="timeError"
      class="mt-1 text-xs text-red-500"
    >
      {{ timeError }}
    </p>

  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue'
import {
  CalendarDaysIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/vue/24/outline'

const props = defineProps({
  minLeadMinutes: {
    type: [Number, String],
    default: 0,
  },
  showSubmitButton: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['schedule', 'selection-change'])

const rootEl = ref(null)

const showDatePicker = ref(false)
const showTimePicker = ref(false)

const today = new Date()
const currentMonth = ref(today.getMonth()) // 0-11
const currentYear = ref(today.getFullYear())

// selectedDate: { year, month, day }
const selectedDate = ref(null)
// selectedTime: { label, hours, minutes }
const selectedTime = ref(null)

const safeMinLeadMinutes = computed(() => {
  const parsed = Number(props.minLeadMinutes)
  if (!Number.isFinite(parsed) || parsed <= 0) return 0
  return Math.floor(parsed)
})

// ----- Labels -----
const monthNames = [
  'January', 'February', 'March', 'April',
  'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December',
]

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const monthLabel = computed(
  () => `${monthNames[currentMonth.value]} ${currentYear.value}`,
)

const dateLabel = computed(() => {
  if (!selectedDate.value) return 'Date'
  const { year, month, day } = selectedDate.value
  const d = new Date(year, month, day)
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
})

const timeLabel = computed(() => {
  if (!selectedTime.value) return 'Time'
  return selectedTime.value.label
})

// ----- Calendar helpers -----
function daysInCurrentMonth() {
  return new Date(currentYear.value, currentMonth.value + 1, 0).getDate()
}

const daysInMonth = computed(() => daysInCurrentMonth())

const leadingBlanks = computed(() => {
  const firstDay = new Date(currentYear.value, currentMonth.value, 1).getDay()
  return firstDay // 0..6 directly matches blanks count
})

function isToday(year, month, day) {
  return (
    year === today.getFullYear() &&
    month === today.getMonth() &&
    day === today.getDate()
  )
}

function isPastDay(day) {
  const candidate = new Date(currentYear.value, currentMonth.value, day)
  // We don't disable "today" itself
  return candidate < new Date(today.getFullYear(), today.getMonth(), today.getDate())
}

function isSelectedDay(day) {
  if (!selectedDate.value) return false
  const { year, month, day: d } = selectedDate.value
  return (
    year === currentYear.value &&
    month === currentMonth.value &&
    d === day
  )
}

function dayClasses(day) {
  const classes = []

  if (isPastDay(day)) {
    classes.push('text-zinc-300 cursor-not-allowed')
  } else {
    classes.push('cursor-pointer text-zinc-700 hover:bg-zinc-100')
  }

  if (isToday(currentYear.value, currentMonth.value, day)) {
    classes.push('border border-zinc-300')
  }

  if (isSelectedDay(day)) {
    classes.push('bg-[#2D8CFF] text-white hover:bg-[#1f7aeb] border-none')
  }

  return classes.join(' ')
}

function prevMonth() {
  if (currentMonth.value === 0) {
    currentMonth.value = 11
    currentYear.value -= 1
  } else {
    currentMonth.value -= 1
  }
}

function nextMonth() {
  if (currentMonth.value === 11) {
    currentMonth.value = 0
    currentYear.value += 1
  } else {
    currentMonth.value += 1
  }
}

function pickDate(day) {
  if (isPastDay(day)) return
  selectedDate.value = {
    year: currentYear.value,
    month: currentMonth.value,
    day,
  }
  if (selectedTime.value && isBlockedTimeOption(selectedTime.value)) {
    selectedTime.value = null
  }
  showDatePicker.value = false
}

// ----- Time list -----
const times = []
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 5) {
    const hour12 = (h % 12) || 12
    const ampm = h < 12 ? 'AM' : 'PM'
    const mm = m.toString().padStart(2, '0')
    const label = `${hour12}:${mm} ${ampm}`
    times.push({ label, hours: h, minutes: m })
  }
}

function minAllowedTimestamp() {
  return Date.now() + (safeMinLeadMinutes.value * 60 * 1000)
}

function isBlockedTimeOption(t) {
  // Need a date chosen to compare against "now"
  if (!selectedDate.value) return false;

  const { year, month, day } = selectedDate.value;
  const now = new Date();

  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth();
  const nowDay = now.getDate();

  // Only restrict times for *today*; future days can pick any time
  if (year !== nowYear || month !== nowMonth || day !== nowDay) {
    return false;
  }

  const candidate = new Date(year, month, day, t.hours, t.minutes, 0);
  return candidate.getTime() <= minAllowedTimestamp();
}

const visibleTimes = computed(() => times.filter((t) => !isBlockedTimeOption(t)))

function pickTime(t) {
  if (isBlockedTimeOption(t)) return; // safety guard
  selectedTime.value = t;
  showTimePicker.value = false;
}

// ----- Visibility toggles -----
function toggleDatePicker() {
  showDatePicker.value = !showDatePicker.value
  if (showDatePicker.value) {
    showTimePicker.value = false
  }
}

function toggleTimePicker() {
  showTimePicker.value = !showTimePicker.value
  if (showTimePicker.value) {
    showDatePicker.value = false
  }
}

// ----- Schedule logic -----
const scheduledDate = computed(() => {
  if (!selectedDate.value || !selectedTime.value) return null
  const { year, month, day } = selectedDate.value
  const { hours, minutes } = selectedTime.value
  // This is a *local* Date at the chosen time
  return new Date(year, month, day, hours, minutes, 0)
})

const isValid = computed(() => {
  if (!scheduledDate.value) return false
  return scheduledDate.value.getTime() > minAllowedTimestamp()
})

const scheduledSqlValue = computed(() => {
  if (!isValid.value || !scheduledDate.value) return null
  return scheduledDate.value.toISOString().slice(0, 19).replace('T', ' ')
})

watch(scheduledSqlValue, (value) => {
  emit('selection-change', value)
}, { immediate: true })

const timeError = computed(() => {
  // Only show an error if both date & time are chosen
  if (!selectedDate.value || !selectedTime.value) return '';
  if (!isValid.value) {
    if (safeMinLeadMinutes.value > 0) {
      return `Please choose a time at least ${safeMinLeadMinutes.value} minutes in the future.`;
    }
    return 'Please choose a time in the future.';
  }
  return '';
});

function submitSchedule() {
  if (!scheduledSqlValue.value) return
  emit('schedule', scheduledSqlValue.value)
}

// ----- Click outside to close dropdowns -----
function handleClickOutside(e) {
  if (!rootEl.value) return
  if (!rootEl.value.contains(e.target)) {
    showDatePicker.value = false
    showTimePicker.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>


<style scoped>

/* ✅ Hover-only, nearly invisible scrollbar */
:deep(.chat-scroll) {
  scrollbar-width: thin;                 /* Firefox */
  scrollbar-color: transparent transparent;
}

/* WebKit base */
:deep(.chat-scroll::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
}

/* ❌ Kill classic buttons (no-op for overlay, harmless otherwise) */
:deep(.chat-scroll::-webkit-scrollbar-button) {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
  background-color: transparent;
}

/* Track fully invisible */
:deep(.chat-scroll::-webkit-scrollbar-track) {
  background: transparent;
}

/* Thumb invisible by default */
:deep(.chat-scroll::-webkit-scrollbar-thumb) {
  background-color: transparent;
  border-radius: 999px;
}

/* ✅ On hover: fade scrollbar in */
:deep(.chat-scroll:hover) {
  scrollbar-color: rgba(203, 213, 225, 0.45) transparent; /* Firefox */
}

:deep(.chat-scroll:hover::-webkit-scrollbar-thumb) {
  background-color: rgba(203, 213, 225, 0.45);
}

/* Slightly stronger on thumb hover */
:deep(.chat-scroll:hover::-webkit-scrollbar-thumb:hover) {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>
